import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

type Provider = "smtp" | "resend";

interface EmailSettings {
  provider: Provider;
  fromEmail: string;
  fromName: string;
  smtp: { host: string; port: number; user: string; secure: boolean; passwordSet: boolean };
  resend: { apiKeySet: boolean };
}

interface FormState {
  provider: Provider;
  fromEmail: string;
  fromName: string;
  smtpHost: string;
  smtpPort: string;
  smtpUser: string;
  smtpPassword: string;
  smtpSecure: boolean;
  resendApiKey: string;
}

function toFormState(s: EmailSettings): FormState {
  return {
    provider: s.provider,
    fromEmail: s.fromEmail,
    fromName: s.fromName,
    smtpHost: s.smtp.host,
    smtpPort: String(s.smtp.port),
    smtpUser: s.smtp.user,
    smtpPassword: "",
    smtpSecure: s.smtp.secure,
    resendApiKey: "",
  };
}

export function AdminEmail() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["admin-email-settings"],
    queryFn: () => api.get<EmailSettings>("/admin/email-settings"),
  });

  const [form, setForm] = useState<FormState | null>(null);
  useEffect(() => {
    if (q.data && !form) setForm(toFormState(q.data));
  }, [q.data, form]);

  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: (f: FormState) =>
      api.put("/admin/email-settings", {
        provider: f.provider,
        fromEmail: f.fromEmail,
        fromName: f.fromName || null,
        smtp: {
          host: f.smtpHost,
          port: Number(f.smtpPort) || 587,
          user: f.smtpUser,
          secure: f.smtpSecure,
          ...(f.smtpPassword ? { password: f.smtpPassword } : {}),
        },
        resend: {
          ...(f.resendApiKey ? { apiKey: f.resendApiKey } : {}),
        },
      }),
    onSuccess: async () => {
      setSaveErr(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      await qc.invalidateQueries({ queryKey: ["admin-email-settings"] });
      setForm(null); // re-seed from the fresh GET (clears password/apiKey inputs, updates *Set flags)
    },
    onError: (e) => setSaveErr(e instanceof ApiError ? e.message : "Failed to save"),
  });

  const [testTo, setTestTo] = useState("");
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
  const sendTest = useMutation({
    mutationFn: () => api.post("/admin/email-settings/test", { to: testTo }),
    onSuccess: () => setTestResult({ ok: true, message: `Test email sent to ${testTo}.` }),
    onError: (e) =>
      setTestResult({ ok: false, message: e instanceof ApiError ? e.message : "Failed to send test email" }),
  });

  if (q.isLoading || !form) {
    return (
      <div className="py-10 text-center text-sm text-muted-foreground inline-flex items-center justify-center w-full gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Mail className="h-3.5 w-3.5 text-muted-foreground" />
            <CardTitle>Outbound email</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-xs text-muted-foreground -mt-2">
            Used for platform email such as org invites. Choose one active provider — its
            credentials are encrypted at rest and never shown again after saving.
          </p>

          <div className="space-y-1.5">
            <Label>Provider</Label>
            <ProviderToggle
              value={form.provider}
              onChange={(provider) => setForm({ ...form, provider })}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="fromEmail">From address</Label>
              <Input
                id="fromEmail"
                type="email"
                value={form.fromEmail}
                onChange={(e) => setForm({ ...form, fromEmail: e.target.value })}
                placeholder="noreply@yourapp.dev"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="fromName">From name <span className="text-subtle-foreground font-normal">(optional)</span></Label>
              <Input
                id="fromName"
                value={form.fromName}
                onChange={(e) => setForm({ ...form, fromName: e.target.value })}
                placeholder="OpenFrontend"
              />
            </div>
          </div>

          {form.provider === "smtp" && (
            <div className="space-y-3 border-t border-border-muted pt-4">
              <div className="text-2xs uppercase tracking-wider text-subtle-foreground">SMTP</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2 space-y-1.5">
                  <Label htmlFor="smtpHost">Host</Label>
                  <Input
                    id="smtpHost"
                    value={form.smtpHost}
                    onChange={(e) => setForm({ ...form, smtpHost: e.target.value })}
                    placeholder="smtp.example.com"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="smtpPort">Port</Label>
                  <Input
                    id="smtpPort"
                    inputMode="numeric"
                    value={form.smtpPort}
                    onChange={(e) => setForm({ ...form, smtpPort: e.target.value.replace(/\D/g, "") })}
                    placeholder="587"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="smtpUser">Username</Label>
                  <Input
                    id="smtpUser"
                    value={form.smtpUser}
                    onChange={(e) => setForm({ ...form, smtpUser: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="smtpPassword">
                    Password
                    {q.data?.smtp.passwordSet && (
                      <span className="text-subtle-foreground font-normal"> (set — leave blank to keep)</span>
                    )}
                  </Label>
                  <Input
                    id="smtpPassword"
                    type="password"
                    value={form.smtpPassword}
                    onChange={(e) => setForm({ ...form, smtpPassword: e.target.value })}
                    placeholder={q.data?.smtp.passwordSet ? "••••••••" : ""}
                  />
                </div>
              </div>
              <Toggle
                label="Use TLS (recommended — port 465, or STARTTLS on 587)"
                checked={form.smtpSecure}
                onChange={(smtpSecure) => setForm({ ...form, smtpSecure })}
              />
            </div>
          )}

          {form.provider === "resend" && (
            <div className="space-y-1.5 border-t border-border-muted pt-4">
              <Label htmlFor="resendApiKey">
                Resend API key
                {q.data?.resend.apiKeySet && (
                  <span className="text-subtle-foreground font-normal"> (set — leave blank to keep)</span>
                )}
              </Label>
              <Input
                id="resendApiKey"
                type="password"
                value={form.resendApiKey}
                onChange={(e) => setForm({ ...form, resendApiKey: e.target.value })}
                placeholder={q.data?.resend.apiKeySet ? "••••••••" : "re_..."}
              />
            </div>
          )}

          {saveErr && <div className="text-xs text-danger bg-danger-subtle border border-danger/30 rounded-md px-2.5 py-1.5">{saveErr}</div>}

          <div className="flex items-center justify-end gap-2 pt-1">
            {saved && (
              <span className="text-xs text-success inline-flex items-center gap-1">
                <Check className="h-3.5 w-3.5" /> Saved
              </span>
            )}
            <Button
              variant="primary"
              disabled={!form.fromEmail || save.isPending}
              onClick={() => save.mutate(form)}
            >
              {save.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              Save
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Send className="h-3.5 w-3.5 text-muted-foreground" />
            <CardTitle>Send a test email</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground -mt-2">
            Verifies the saved configuration actually sends. Save your changes above first.
          </p>
          <div className="flex items-end gap-2">
            <div className="flex-1 min-w-0 space-y-1.5">
              <Label htmlFor="testTo">Send to</Label>
              <Input
                id="testTo"
                type="email"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="you@example.com"
              />
            </div>
            <Button
              variant="default"
              className="shrink-0"
              disabled={!testTo || sendTest.isPending}
              onClick={() => { setTestResult(null); sendTest.mutate(); }}
            >
              {sendTest.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Send test
            </Button>
          </div>
          {testResult && (
            <div
              className={cn(
                "text-xs rounded-md px-2.5 py-1.5 border",
                testResult.ok
                  ? "text-success bg-success-subtle border-success/30"
                  : "text-danger bg-danger-subtle border-danger/30",
              )}
            >
              {testResult.message}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ProviderToggle({ value, onChange }: { value: Provider; onChange: (p: Provider) => void }) {
  const options: { key: Provider; label: string }[] = [
    { key: "smtp", label: "SMTP" },
    { key: "resend", label: "Resend" },
  ];
  return (
    <div className="inline-flex rounded-md border border-border p-0.5 bg-subtle/40">
      {options.map((opt) => (
        <button
          key={opt.key}
          type="button"
          onClick={() => onChange(opt.key)}
          className={cn(
            "px-3 h-7 text-xs font-medium rounded-[5px] transition-colors",
            value === opt.key
              ? "bg-background text-foreground shadow-sm border border-border-muted"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between gap-3 cursor-pointer text-sm">
      <span className="text-xs">{label}</span>
      <span
        className={cn(
          "relative inline-flex h-5 w-9 items-center rounded-full transition-colors shrink-0",
          checked ? "bg-accent" : "bg-border",
        )}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only"
        />
        <span
          className={cn(
            "inline-block h-4 w-4 rounded-full bg-background shadow transition-transform",
            checked ? "translate-x-[18px]" : "translate-x-0.5",
          )}
        />
      </span>
    </label>
  );
}
